import { FC, useEffect, useState } from 'react';
import { Variant as VariantType } from 'shared/types/product';
import { useFormat } from 'helpers/hooks/useFormat';
import { filterAttributeBasedVariants } from '../helpers/filterAttributeBasedVariants';
import Dropdown, { Option } from 'components/commercetools-ui/atoms/dropdown';
import { COLOR, ITEM_NAME } from 'helpers/constants/attributes';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useParams } from 'next/navigation';

type ProductVariantProps = {
  className?: string;
  currentVariant: VariantType;
  variants: VariantType[];
  attribute: string;
  inModalVersion?: boolean;
  onClick?: (sku: string) => void;
};

const ProductVariant: FC<ProductVariantProps> = ({
  className,
  currentVariant,
  variants,
  attribute,
  inModalVersion,
  onClick,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });

  const [variantsToUse, setVariantsToUse] = useState<VariantType[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>(currentVariant.sku);

  const attributeToFilterTo = attribute === 'size' ? 'colorlabel' : 'size';

  useEffect(() => {
    const filteredVariants: VariantType[] = currentVariant.attributes?.['size']
      ? filterAttributeBasedVariants(variants, currentVariant, attributeToFilterTo)
      : variants; // Include all variants, allowing duplicates
    setVariantsToUse(filteredVariants);
  }, [variants, attribute, currentVariant, attributeToFilterTo]);

  useEffect(() => {
    setSelectedOption(currentVariant.sku);
  }, [currentVariant]);

  const sortVariantsCmp = (a: VariantType, b: VariantType) => {
    const first = a.attributes?.[attribute];
    const second = b.attributes?.[attribute];

    if (first > second) return 1;
    if (first < second) return -1;
    return 0;
  };

  const handleSelectChange = (option: Option) => {
    if (option.value !== selectedOption) {
      //Remove script for ShopOnline button when dropdown value changed
      removeScript();
      //Remove button class (ShopOnline)
      const button = document.querySelector(
        'button.cswidget'
      );      
      if (button) {
        const instanceId = button.getAttribute('data-instanceid');
        if (instanceId) {
          // Remove the class corresponding to the data-instanceid value
          button.classList.remove(instanceId);
          button.removeAttribute('data-instanceid');
        }
      } 

      const selectedVariant = variants.find((variant) => variant.sku === option.value);
      if (selectedVariant) {
        setSelectedOption(selectedVariant.sku);
        addScript();
        onClick?.(selectedVariant.sku);      
      }
    }
  };


  
    //Added script for ShopOnline button when dropdown value changed
    const addScript = () => {
      if (!document.getElementById('cswidgetloaderjs')) {
        const script = document.createElement('script');
        script.id = 'cswidgetloaderjs';
        script.src = 'https://cdn.channelsight.com/widget/scripts/cswidget.loader.js';
        script.async = true;
        document.body.appendChild(script);
      }
    };

    // Clean-up function to remove the script
    const removeScript = () => {
      const cswidgetloaderjsscript = document.getElementById('cswidgetloaderjs');
      const cswidgetjsscript = document.getElementById('cswidgetjs');
      if (cswidgetloaderjsscript) {
        document.body.removeChild(cswidgetloaderjsscript);
      }
      if (cswidgetjsscript) {
        document.body.removeChild(cswidgetjsscript);
      }
    };




  const selectOptions: Option[] =
    variantsToUse?.sort(sortVariantsCmp).map(({ attributes, sku }) => ({
      // Include itemCode in the mapping function
      name: attributes?.[attribute], // Use the attribute value as the name
      value: sku,
      imageUrl: `/images/colorswatches/swatch_${attributes?.[COLOR]?.replaceAll(' ', '').replaceAll('/', '')}.png`,
      isSelected: sku === selectedOption, // Check if the option is selected
      itemCode: attributes?.['item-code'], // Include itemCode
    })) || [];

  return (
    <div className={className}>
      {variantsToUse && variantsToUse.length > 1 ? (
        <div className="mt-16 flex flex-col gap-8 lg:mt-8 lg:flex-row lg:gap-22 2xl:mt-16 2xl:gap-32">
          <Dropdown
            selectOptions={selectOptions}
            selectOnChange={handleSelectChange}
            value={selectedOption}
            selectDropdownClassNames="rounded-lg w-full lg:!w-[13rem] xl:!w-[15rem] 3xl:!w-[20rem]"
            optionsClassName="!top-45 xl:!top-[34px] 2xl:!top-[34px] md:!top-44 !z-[50] "
            imageUrl="imageUrl"
          />
        </div>
      ) : (
        <div className="mt-16 flex flex-col gap-8 lg:mt-8 lg:flex-row lg:gap-22 2xl:mt-16 2xl:gap-32">
          <div className="flex items-center gap-8 hover:cursor-pointer">
            <img
              src={`/images/colorswatches/swatch_${currentVariant.attributes?.[COLOR]?.replaceAll(' ', '').replaceAll(
                '/',
                '',
              )}.png`}
              alt={currentVariant.attributes?.[attribute]}
              className="h-32 w-32 rounded-full 2xl:h-40 2xl:w-40"
            />
            <div className="text-14 font-semibold leading-[3rem] text-[#333] underline underline-offset-2 lg:text-12 2xl:text-14">
              {currentVariant.attributes?.[attribute]}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductVariant;
